import { useTabSyncManager } from './TabSyncManager';
import type { BaseBroadcastMessage, UnsubscribeFn, ITabSyncManager } from './types';

// Base class for creating sync managers that communicate across tabs
export abstract class BaseSyncManager<TMessage extends BaseBroadcastMessage> {
  protected tabSync: ITabSyncManager;
  protected unsubscribers: UnsubscribeFn[] = [];
  protected isInitialized = false;

  // Channel name for routing messages
  protected abstract readonly channel: string;

  // Handle incoming broadcast messages
  protected abstract handleMessage(msg: TMessage): void;

  protected constructor() {
    this.tabSync = useTabSyncManager();
  }

  // Whether this tab is the leader
  protected get isLeader(): boolean {
    return this.tabSync.isLeader;
  }

  // Called when this tab becomes the leader
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onBecomeLeader(): void {}

  // Called when this tab loses leadership
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onLoseLeadership(): void {}

  // Called when tab becomes visible
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTabVisible(): void {}

  // Called when tab becomes hidden
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onTabHidden(): void {}

  // Initialize the sync manager
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!this.tabSync.isInitialized) {
      await this.tabSync.initialize();
    }

    this.unsubscribers.push(
      this.tabSync.subscribe<TMessage>(this.channel, (msg) => this.handleMessage(msg))
    );

    this.unsubscribers.push(
      this.tabSync.onLeadershipChange(({ isLeader }) => {
        if (isLeader) {
          this.onBecomeLeader();
        } else {
          this.onLoseLeadership();
        }
      })
    );

    this.unsubscribers.push(
      this.tabSync.onVisibilityChange(({ isVisible }) => {
        if (isVisible) {
          this.onTabVisible();
        } else {
          this.onTabHidden();
        }
      })
    );

    if (this.isLeader) {
      this.onBecomeLeader();
    }

    this.isInitialized = true;
  }

  // Broadcast a message to all tabs
  protected broadcast(type: string, payload?: TMessage['payload']): Promise<void> {
    return this.tabSync.broadcast({
      channel: this.channel,
      type,
      payload,
    } as TMessage);
  }

  // Clean up all subscriptions
  public destroy(): void {
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers = [];
    this.isInitialized = false;
  }
}
